import React from 'react';
import { ProductsTable } from './ProductsTable';
import { ProductGrid } from './ProductGrid';
import { ProductsFilterState, createDefaultFilterState } from '@/types/filters';
import { useQuery } from '@tanstack/react-query';
import { productService } from '@/services/productService';

interface ProductsTableAdapterProps {
  viewMode?: 'list' | 'grid';
  filters?: ProductsFilterState;
  hideTopSearch?: boolean;
  hideTopControls?: boolean;
  onFilterChange?: (columnId: string, value: any) => void;
  onClearFilters?: () => void;
}

/**
 * This is an adapter component that makes the existing ProductsTable work
 * with the new unified filter architecture.
 *
 * Implementation notes:
 * - Bridges between unified ProductsFilterState and existing components
 * - Handles filter format conversions for compatibility
 * - Ensures consistent filter behavior across views
 * - Fetches and provides API-sourced brands to fix filter dropdown issues
 */
export function ProductsTableAdapter({
  viewMode = 'list',
  filters,
  hideTopSearch = false,
  hideTopControls = false,
  onFilterChange,
  onClearFilters,
}: ProductsTableAdapterProps) {
  // Get ALL brands from API (not filtered by current products)
  const { data: allBrands = [] } = useQuery({
    queryKey: ['brands'],
    queryFn: () => productService.searchBrands(''), // Empty string gets all brands
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Convert API brand response to simple string array
  const uniqueBrands = React.useMemo(() => {
    return allBrands.map(brand => brand.value).sort();
  }, [allBrands]);

  // Use default filters if not provided
  const normalizedFilters = React.useMemo(() => {
    if (!filters) {
      return createDefaultFilterState(viewMode, '', {
        all: 'all',
        active: 'active',
        inactive: 'inactive',
      });
    }

    // Convert ProductsFilterState to the format expected by existing components
    return Object.entries(filters).reduce(
      (acc, [key, value]) => {
        // Convert null values to undefined and skip undefined values
        if (value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, any>
    );
  }, [filters, viewMode]);

  // Switch between ProductsTable and ProductGrid based on viewMode
  if (viewMode === 'grid') {
    return (
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <ProductGrid
          filters={normalizedFilters as Record<string, any>}
          products={[]}
          loading={false}
          error={null}
          onFilterChange={onFilterChange || (() => {})}
          onClearFilters={onClearFilters || (() => {})}
          table={undefined}
          columns={[]}
          uniqueCategories={[]}
          uniqueTags={[]}
          uniqueBrands={uniqueBrands}
          families={[]}
          isFamiliesLoading={false}
        />
      </div>
    );
  }

  // Default to list view
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <ProductsTable
        hideTopSearch={hideTopSearch}
        hideTopControls={hideTopControls}
        filters={normalizedFilters as Record<string, unknown>}
        uniqueBrands={uniqueBrands}
      />
    </div>
  );
}
